import { createFileRoute } from "@tanstack/react-router";
import {
  createTickets,
  getTicketConversation,
  getUserTicketId,
  ticketNotificationsCountQueryKey,
} from "@/queries/tickets";
import { useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { type Message, groupMessagesByDate } from "@/lib/utils";

import dayjs from "dayjs";
import jalaliDay from "jalaliday";
dayjs.extend(jalaliDay);

import { toast } from "sonner";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { LoadingSpinner } from "@/components/ui/loading";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  Loader2,
  Bot,
  Paperclip,
  X,
  Send,
  ChevronLeft,
  LockKeyhole,
  MessageCircle,
} from "lucide-react";
import { useForm } from "react-hook-form";
import TicketMessage from "@/components/ticket-message";
import type { TicketInfo } from "@/types";
import { TicketStatus } from "@/types";

export const Route = createFileRoute("/support/tickets/$id")({
  component: ShowTicket,
});

const statusStyles = {
  answering:
    "border-sky-300 bg-sky-100 text-sky-800 dark:border-sky-700 dark:bg-sky-900/60 dark:text-sky-200",
  answered:
    "border-emerald-300 bg-emerald-100 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-200",
  closed:
    "border-red-300 bg-red-100 text-red-800 dark:border-red-700 dark:bg-red-900/60 dark:text-red-200",
};

function ShowTicket() {
  const { id } = Route.useParams();
  const [messages, setMessages] = useState<Array<Message>>([]);
  const [images, setImages] = useState<File[]>([]);
  const [previewImages, setPreviewImages] = useState<string[]>([]);

  const endMessageRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    endMessageRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const ticketQuery = useQuery({
    queryFn: () => getUserTicketId(id),
    queryKey: ["get", "ticket", id],
  });

  const ticket = ticketQuery.data?.data?.data?.[0] as TicketInfo | undefined;
  const isEsaj = ticket?.is_esaj === true || ticket?.is_esaj === 1;
  const groupedMessage = useMemo(
    () => groupMessagesByDate(messages),
    [messages],
  );

  const conversationQuery = useQuery({
    queryFn: () => getTicketConversation(id),
    queryKey: ["get", "ticket", "conversation", id],
  });

  useEffect(() => {
    if (conversationQuery.data) {
      const data = conversationQuery.data.data.data;
      if (data) {
        setMessages((current) => {
          const localMessages = current.filter((message) => message.id < 0);
          const unsyncedLocalMessages = localMessages.filter((localMessage) => {
            return !data.some((serverMessage: Message) => {
              if (
                serverMessage.is_admin ||
                serverMessage.message !== localMessage.message
              ) {
                return false;
              }

              const timeDifference = Math.abs(
                new Date(serverMessage.created_at).getTime() -
                  new Date(localMessage.created_at).getTime(),
              );

              return timeDifference < 120_000;
            });
          });

          return [...data, ...unsyncedLocalMessages];
        });
        // بازکردن مکالمه، پیام‌های ورودیِ این تیکت را خوانده‌شده می‌کند؛
        // پس از دریافت موفق مکالمه، شمارنده پیام‌های خوانده‌نشده دوباره دریافت شود.
        queryClient.invalidateQueries({
          queryKey: ticketNotificationsCountQueryKey,
        });
      }
    }
  }, [conversationQuery.data]);

  const answerMutation = useMutation({
    mutationFn: ({ formData }: { formData: FormData; message: Message }) =>
      createTickets(formData),
    mutationKey: ["answer", "ticket"],
    onMutate({ message }) {
      setMessages((current) => [...current, message]);
      form.reset();
      setImages([]);
      setPreviewImages([]);
    },
    onSuccess(_, { message }) {
      toast.success("پاسخ با موفقیت ارسال شد", {
        closeButton: true,
      });
      setMessages((current) =>
        current.map((item) =>
          item.id === message.id ? { ...item, deliveryStatus: "sent" } : item,
        ),
      );
    },
    onError(_, { message }) {
      setMessages((current) =>
        current.map((item) =>
          item.id === message.id ? { ...item, deliveryStatus: "error" } : item,
        ),
      );
      toast.error("ارسال پیام ناموفق بود");
    },
  });

  const form = useForm();

  const handleChangeImage = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);

    setImages((prev) => [...prev, ...files]);
    const previews = files.map((file) => URL.createObjectURL(file));
    setPreviewImages((prev) => [...prev, ...previews]);
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
    setPreviewImages((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = (values: any) => {
    if (id) {
      const formData = new FormData();
      formData.append("message", values.message);
      formData.append("ticket_id", id);
      images.forEach((img, index) => {
        formData.append(`images[${index}]`, img);
      });

      const optimisticMessage: Message = {
        id: -Date.now(),
        message: values.message,
        is_admin: false,
        created_at: new Date().toISOString(),
        deliveryStatus: "sending",
        previewImages: [...previewImages],
      };

      answerMutation.mutate({ formData, message: optimisticMessage });
    }
  };

  return (
    <>
      <Card className="flex h-[90svh] w-full min-w-0 max-w-full flex-col overflow-hidden border-border/60 shadow-sm lg:h-auto">
        <div className="flex shrink-0 items-center justify-between gap-2 p-4 pb-2 lg:p-6 lg:pb-2">
          <div className="flex items-center gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              {isEsaj ? (
                <Bot className="size-5" />
              ) : (
                <MessageCircle className="size-5" />
              )}
            </div>
            <div>
              <CardTitle className="text-base">
                {isEsaj ? ticket?.title || "ربات ایساج" : "مشاهده تیکت"}
              </CardTitle>
              {ticket?.status && (
                <Badge
                  className={`mt-1 gap-1 text-[11px] ${statusStyles[ticket.status as keyof typeof TicketStatus]}`}
                >
                  {TicketStatus[ticket.status as keyof typeof TicketStatus]}
                </Badge>
              )}
            </div>
          </div>
          <Link to="/support/tickets">
            <Button variant="outline" className="rounded-full">
              بازگشت
              <ChevronLeft className="mr-1 size-4" />
            </Button>
          </Link>
        </div>
        <Separator className="my-2 shrink-0" />

        <CardContent className="flex min-h-0 min-w-0 max-w-full flex-1 flex-col overflow-hidden lg:flex-none">
          {conversationQuery.isPending ? (
            <div className="flex justify-center py-10">
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <div className="relative mb-4 flex min-h-0 flex-1 flex-col overflow-y-auto rounded-2xl border border-border/60 bg-[radial-gradient(circle_at_1px_1px,hsl(var(--muted-foreground)/0.08)_1px,transparent_0)] bg-[size:22px_22px] bg-muted/20 px-3 py-4 sm:px-5 lg:max-h-[32rem] lg:min-h-96 lg:flex-none">
                {Object.keys(groupedMessage).length === 0 ? (
                  <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
                    هنوز پیامی ثبت نشده است
                  </div>
                ) : (
                  Object.keys(groupedMessage).map((date) => (
                    <div key={date} className="space-y-1">
                      <div className="sticky top-1 z-10 my-3 flex justify-center">
                        <span className="rounded-full border border-border/40 bg-background/80 px-3 py-1 text-[11px] font-medium text-muted-foreground shadow-sm backdrop-blur-md">
                          {dayjs(date)
                            .calendar("jalali")
                            .locale("fa")
                            .format("dddd DD MMMM YYYY")}
                        </span>
                      </div>
                      <div className="space-y-2">
                        {groupedMessage[date].map((message) => (
                          <TicketMessage
                            message={message}
                            isEsaj={isEsaj}
                            key={message.id}
                          />
                        ))}
                      </div>
                    </div>
                  ))
                )}
                <div ref={endMessageRef} />
              </div>

              {ticketQuery?.isFetching ? (
                <div className="flex justify-center items-center">
                  <LoadingSpinner />
                </div>
              ) : ticket?.status === "closed" ? (
                <div className="flex shrink-0 items-center justify-center gap-2 rounded-2xl border border-dashed border-border/60 bg-muted/30 py-3 text-sm text-muted-foreground">
                  <LockKeyhole className="size-4" />
                  این تیکت بسته شده
                </div>
              ) : isEsaj ? (
                <div className="mb-2 flex shrink-0 items-center justify-center gap-2 rounded-2xl border border-dashed border-border/60 bg-muted/30 py-3 text-sm text-muted-foreground">
                  <Bot className="size-4" />
                  این پیام به‌صورت خودکار توسط ربات ایساج ارسال شده و امکان
                  پاسخ‌دادن به آن وجود ندارد.
                </div>
              ) : (
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="w-full min-w-0 max-w-full shrink-0 overflow-hidden"
                >
                  <div className="flex min-w-0 max-w-full flex-col gap-3">
                    {previewImages.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {previewImages.map((src, i) => (
                          <div
                            key={i}
                            className="relative size-20 overflow-hidden rounded-xl border border-border/60 shadow-sm"
                          >
                            <img
                              src={src}
                              alt="preview"
                              className="size-full object-cover"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(i)}
                              className="absolute left-1 top-1 rounded-full bg-black/60 p-1 text-white"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex w-full min-w-0 max-w-full items-end gap-2 overflow-hidden rounded-2xl border border-border/60 bg-background p-2 shadow-sm">
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="size-9 shrink-0 rounded-full"
                        asChild
                      >
                        <label className="cursor-pointer">
                          <Paperclip className="size-4" />
                          <input
                            hidden
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleChangeImage}
                          />
                        </label>
                      </Button>

                      <Textarea
                        {...form.register("message", {
                          required: "وارد کردن این فیلد الزامی است",
                          })}
                          placeholder="پیام خود را بنویسید..."
                          rows={3}
                          className="min-h-20 w-0 min-w-0 max-w-full flex-1 resize-y border-0 bg-transparent shadow-none focus-visible:ring-0"
                        />

                      <Button
                        type="submit"
                        size="icon"
                        disabled={answerMutation.isPending}
                        aria-label="ارسال پیام"
                        className="size-11 shrink-0 rounded-full bg-gradient-to-br from-sky-400 to-blue-600 text-white shadow-md shadow-sky-500/25 ring-2 ring-sky-100 transition-transform hover:scale-105 hover:from-sky-500 hover:to-blue-700 dark:ring-sky-950"
                      >
                        {answerMutation.isPending ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : (
                          <Send className="size-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </form>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
}
